import { Injectable } from '@angular/core';
import { AppState } from '@app/reducers';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { environment } from '@environments/environment';
import { loggedInUserData } from '@modules/others/auth.selectors';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { LoggedInUserModel, encryptData } from '../utils';
import * as CryptoJS from "crypto-js";
import { ChatEvents } from '../enums/enum';

@Injectable({
  providedIn: 'root'
})
export class SignalrConnectionService {
  connection: HubConnection;
  private signalEventReceiveSubject = new BehaviorSubject(null);
  private signalChatEventReceiveSubject = new BehaviorSubject(null);
  hubConnectionForSalesAPI: HubConnection;
  hubConnectionForTelephonyAPI: HubConnection;
  hubConnectionForChatAPI: HubConnection;
  loggedInUserData: LoggedInUserModel;

  // Connection state tracking for rate limiting
  private salesAPIConnectionAttempts = 0;
  private salesAPILastAttemptTime = 0;
  private salesAPIMaxRetries = 5;
  private salesAPIMinRetryDelay = 10000; // 10 seconds minimum
  private salesAPIMaxRetryDelay = 300000; // 5 minutes maximum
  private salesAPIConnectionInProgress = false;

  constructor(private store: Store<AppState>) {
    this.combineLatestNgrxStoreData();
  }

  combineLatestNgrxStoreData() {
    combineLatest([
      this.store.select(loggedInUserData)
    ]).subscribe((response) => {
      this.loggedInUserData = new LoggedInUserModel(response[0]);
      
      // Auto-initialize Sales API hub connection when user is logged in
      if (this.loggedInUserData?.userId && !this.hubConnectionForSalesAPI) {
        this.hubConnectionInitializationForSalesAPIPromise();
      }
    });
  }

  hubConnection(): void {
    this.connection = new HubConnectionBuilder()
      .withUrl(environment.SALES_API, {
      }).build();
    this.connection.start()
      .then(() => {
        this.triggerEvent()
      })
      .catch((error) => {
        console.log('Connection Failed : ', error);
      });
    this.connection.onclose(() => {
      // this.hubConnection();
    });
  }

  hubConnectionForTelephonyAPIPromise: Promise<void>;
  hubConnectionInitializationForTelephonyAPIPromise() {
    // Reuse the hub instance
    if (this.hubConnectionForTelephonyAPIPromise) {
      return this.hubConnectionForTelephonyAPIPromise;
    }
    else {
      this.buildHubConnection('telephony');
      this.hubConnectionForTelephonyAPIPromise = this.hubConnectionForTelephonyAPI.start();
      this.hubConnectionForTelephonyAPIPromise.
        then(() => console.log('Telephony API Hub Connection is started..!!'))
        .catch((error) => {
          console.log('Telephony API Hub Connection is Failed because of : ', error);
        });
      this.hubConnectionForTelephonyAPI.onclose(() => {
        console.log('Telephony API Hub Connection is closed..!!');
        if (this.loggedInUserData?.userId) {
          this.hubConnectionForTelephonyAPIPromise = undefined;
          this.hubConnectionForTelephonyAPI = undefined;
          this.hubConnectionInitializationForTelephonyAPIPromise();
        }
      });
      return this.hubConnectionForTelephonyAPIPromise;
    }
  }

  salesAPIHubConnectionBuiltInstance() {
    if (this.hubConnectionForSalesAPI) {
      return this.hubConnectionForSalesAPI;
    }
    else {
      this.buildHubConnection('sales');
    }
  }

  hubConnectionForSalesAPIPromise: Promise<void>;
  hubConnectionInitializationForSalesAPIPromise() {
    // Prevent multiple simultaneous connection attempts
    if (this.salesAPIConnectionInProgress) {
      console.log('Sales API Hub connection already in progress, skipping...');
      return this.hubConnectionForSalesAPIPromise;
    }

    // Reuse the hub instance if already connected or connecting
    if (this.hubConnectionForSalesAPIPromise && this.hubConnectionForSalesAPI?.state !== 'Disconnected') {
      return this.hubConnectionForSalesAPIPromise;
    }

    // Check rate limiting
    const now = Date.now();
    const timeSinceLastAttempt = now - this.salesAPILastAttemptTime;
    
    if (this.salesAPIConnectionAttempts >= this.salesAPIMaxRetries) {
      const delay = this.calculateRetryDelay();
      if (timeSinceLastAttempt < delay) {
        console.log(`Rate limit reached. Waiting ${Math.ceil((delay - timeSinceLastAttempt) / 1000)} seconds before next attempt...`);
        setTimeout(() => {
          this.resetSalesAPIConnectionAttempts();
          this.hubConnectionInitializationForSalesAPIPromise();
        }, delay - timeSinceLastAttempt);
        return Promise.reject(new Error('Rate limited - retry later'));
      }
    }

    this.salesAPIConnectionInProgress = true;
    this.salesAPIConnectionAttempts++;
    this.salesAPILastAttemptTime = now;

    console.log(`Sales API Hub connection attempt ${this.salesAPIConnectionAttempts}/${this.salesAPIMaxRetries}`);

    this.buildHubConnection('sales');
    
    // Handle reconnection events
    this.hubConnectionForSalesAPI.onreconnecting(() => {
      console.log('Sales API Hub Connection is reconnecting...');
    });

    this.hubConnectionForSalesAPI.onreconnected(() => {
      console.log('Sales API Hub Connection is reconnected..!!');
      this.resetSalesAPIConnectionAttempts();
      this.createSalesAPIServerEvents(); // Re-register events after reconnection
    });

    this.hubConnectionForSalesAPIPromise = this.hubConnectionForSalesAPI.start();
    this.hubConnectionForSalesAPIPromise
      .then(() => {
        console.log('Sales API Hub Connection is started..!!');
        this.resetSalesAPIConnectionAttempts();
        this.createSalesAPIServerEvents();
        this.salesAPIConnectionInProgress = false;
      })
      .catch((error) => {
        console.log('Sales API Hub Connection is Failed because of : ', error);
        this.salesAPIConnectionInProgress = false;
        
        // Reset the promise to allow retry
        this.hubConnectionForSalesAPIPromise = undefined;
        this.hubConnectionForSalesAPI = undefined;
        
        // Handle 429 errors with longer delays
        if (error.message?.includes('Too Many Requests') || error.status === 429) {
          console.log('Rate limit hit (429), using extended backoff...');
          this.salesAPIMinRetryDelay = Math.min(this.salesAPIMinRetryDelay * 2, 60000); // Increase minimum delay up to 1 minute
        }
        
        // Retry with exponential backoff if user is still logged in and under max retries
        if (this.loggedInUserData?.userId && this.salesAPIConnectionAttempts < this.salesAPIMaxRetries) {
          const retryDelay = this.calculateRetryDelay();
          console.log(`Retrying Sales API Hub Connection in ${retryDelay / 1000} seconds...`);
          setTimeout(() => {
            this.hubConnectionInitializationForSalesAPIPromise();
          }, retryDelay);
        } else if (this.salesAPIConnectionAttempts >= this.salesAPIMaxRetries) {
          console.log('Max retry attempts reached for Sales API Hub. Manual reconnection required.');
        }
      });

    this.hubConnectionForSalesAPI.onclose(() => {
      console.log('Sales API Hub Connection is closed..!!');
      if (this.loggedInUserData?.userId && !this.salesAPIConnectionInProgress) {
        // Only auto-reconnect if not already attempting to connect
        this.hubConnectionForSalesAPI = undefined;
        this.hubConnectionForSalesAPIPromise = undefined;
        
        // Add a small delay before auto-reconnect to prevent rapid reconnection attempts
        setTimeout(() => {
          this.hubConnectionInitializationForSalesAPIPromise();
        }, 2000);
      }
    });

    return this.hubConnectionForSalesAPIPromise;
  }

  telephonyAPIHubConnectionBuiltInstance() {
    if (this.hubConnectionForTelephonyAPI) {
      return this.hubConnectionForTelephonyAPI;
    }
    else {
      this.buildHubConnection('telephony');
    }
  }

  // Helper methods for connection retry logic
  private calculateRetryDelay(): number {
    // Exponential backoff with jitter: base * 2^attempt + random jitter
    const baseDelay = this.salesAPIMinRetryDelay;
    const exponentialDelay = baseDelay * Math.pow(2, this.salesAPIConnectionAttempts - 1);
    const jitter = Math.random() * 0.3 * exponentialDelay; // 30% jitter
    const delay = Math.min(exponentialDelay + jitter, this.salesAPIMaxRetryDelay);
    
    return Math.floor(delay);
  }

  private resetSalesAPIConnectionAttempts(): void {
    this.salesAPIConnectionAttempts = 0;
    this.salesAPIMinRetryDelay = 10000; // Reset to original minimum delay
    this.salesAPIConnectionInProgress = false;
  }

  buildHubConnection(type: string) {
    if (type == 'sales') {
      this.hubConnectionForSalesAPI = new HubConnectionBuilder()
        .configureLogging(LogLevel.Critical)
        .withAutomaticReconnect([0, 10000, 30000, 60000, 120000]) // More conservative auto-reconnect with longer delays
        .withUrl(environment.SALES_API)
        .build();
      this.hubConnectionForSalesAPI.keepAliveIntervalInMilliseconds = 30000000;
      this.hubConnectionForSalesAPI.serverTimeoutInMilliseconds = 30000000;
    }
    else if (type == 'telephony') {
      this.hubConnectionForTelephonyAPI = new HubConnectionBuilder()
        .configureLogging(LogLevel.Critical)
        .withUrl(environment.telephonyApi)
        .build();
    }
    else if (type == 'chat') {
      this.hubConnectionForChatAPI = new HubConnectionBuilder()
        // .configureLogging(LogLevel.Critical)
        .withAutomaticReconnect([0, 2000, 5000, 10000])// Auto-reconnect after 2s, 5s, 10s
        .withUrl(environment.COMMON_API, {
          accessTokenFactory: () => {
            return this.generateAccessToken(this.loggedInUserData?.userId);
          }
        }).build();
        this.hubConnectionForChatAPI.serverTimeoutInMilliseconds = 30000000; // 500 mins
        this.hubConnectionForChatAPI.keepAliveIntervalInMilliseconds = 30000000; // 500 mins
    }
  }

  // for chat start connection
  generateAccessToken(userName) {
    var header = {
      "alg": "HS256",
      "typ": "JWT"
    };
    var stringifiedHeader = CryptoJS.enc.Utf8.parse(JSON.stringify(header));
    var encodedHeader = this.base64url(stringifiedHeader);
    // customize your JWT token payload here
    var data = {
      "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier": userName,
      "exp": 1699819025,
      'admin': 0
    };
    var stringifiedData = CryptoJS.enc.Utf8.parse(JSON.stringify(data));
    var encodedData = this.base64url(stringifiedData);
    var token = encodedHeader + "." + encodedData;
    var secret = "myfunctionauthtest"; // do not expose your secret here
    var signature = CryptoJS.HmacSHA256(token, secret);
    signature = this.base64url(signature);
    var signedToken = token + "." + signature;
    return signedToken;
  }

  base64url(source) {
    // Encode in classical base64
    let encodedSource = CryptoJS.enc.Base64.stringify(source);
    // Remove padding equal characters
    encodedSource = encodedSource.replace(/=+$/, '');
    // Replace characters according to base64url specifications
    encodedSource = encodedSource.replace(/\+/g, '-');
    encodedSource = encodedSource.replace(/\//g, '_');
    return encodedSource;
  }

  chatAPIHubConnectionBuiltInstance() {
    if (this.hubConnectionForChatAPI) {
      return this.hubConnectionForChatAPI;
    }
    else {
      this.buildHubConnection('chat');
    }
  }

  hubConnectionForChatAPIPromise: Promise<void>;
  hubConnectionInitializationForChatAPIPromise() {
    // Reuse the hub instance
    if (this.hubConnectionForChatAPIPromise) {
      return this.hubConnectionForChatAPIPromise;
    }
    else {
      this.buildHubConnection('chat');
      this.hubConnectionForChatAPIPromise = this.hubConnectionForChatAPI.start();
      this.hubConnectionForChatAPIPromise.
        then(() => {
          console.log('Chat API Hub Connection is started..!!');
          this.createChatRoomServerEvents();
        })
        .catch((error) => {
          //console.log('Chat API Hub Connection is Failed because of : ', error);
        });
      this.hubConnectionForChatAPI.onclose(() => {
        console.log('Chat API Hub Connection is closed..!!');
        if (this.loggedInUserData?.userId) {
          this.hubConnectionForChatAPI = undefined;
          this.hubConnectionForChatAPIPromise = undefined;
          this.hubConnectionInitializationForChatAPIPromise();
        }
      });
      return this.hubConnectionForChatAPIPromise;
    }
  }
  // for chat end connection

  closeAllHubConnections() {
    this.closeChatConnection();
    this.closeSalesAPIHubConnection();
    this.hubConnectionForTelephonyAPI?.stop();
    this.hubConnectionForTelephonyAPI = undefined;
    this.hubConnectionForTelephonyAPIPromise = undefined;
  }

  closeSalesAPIHubConnection() {
    // Remove all event listeners before stopping
    this.hubConnectionForSalesAPI?.off('notifyEvent');
    this.hubConnectionForSalesAPI?.off('ReceiveEvents');
    this.hubConnectionForSalesAPI?.off('DispatchingAlertCallBackTrigger');
    this.hubConnectionForSalesAPI?.off('OSCCEmailNotification');
    this.hubConnectionForSalesAPI?.off('eventName');
    this.hubConnectionForSalesAPI?.stop();
    this.hubConnectionForSalesAPI = undefined;
    this.hubConnectionForSalesAPIPromise = undefined;
    this.salesAPIConnectionInProgress = false; // Reset connection progress flag
  }

  isSalesAPIHubConnected(): boolean {
    return this.hubConnectionForSalesAPI?.state === 'Connected';
  }

  getSalesAPIHubState(): string {
    return this.hubConnectionForSalesAPI?.state || 'Disconnected';
  }

  // Manual reconnection method for Sales API
  reconnectSalesAPIHub(): Promise<void> {
    console.log('Manually reconnecting Sales API Hub...');
    this.closeSalesAPIHubConnection();
    this.resetSalesAPIConnectionAttempts(); // Reset retry counter for manual reconnection
    return this.hubConnectionInitializationForSalesAPIPromise();
  }

  closeChatConnection() {
    this.hubConnectionForChatAPI?.off('newMessage');
    this.hubConnectionForChatAPI?.off('getOnlineusers');
    this.hubConnectionForChatAPI?.off('userDetails');
    this.hubConnectionForChatAPI?.off('addedInGroup');
    this.hubConnectionForChatAPI?.off('bradcastCreated');
    this.hubConnectionForChatAPI?.off('onDisconnect');
    this.hubConnectionForChatAPI?.off('removedFromGroup');
    this.hubConnectionForChatAPI?.off('documentReceived');
    this.hubConnectionForChatAPI?.stop();
    this.hubConnectionForChatAPI = undefined;
    this.hubConnectionForChatAPIPromise = undefined;
  }

  closeTelephonyAPIHubConnection() {
    this.hubConnectionForTelephonyAPI?.stop();
    this.hubConnectionForTelephonyAPI = undefined;
    this.hubConnectionForTelephonyAPIPromise = undefined;
  }

  closeChatAPIHubConnection() {
    this.hubConnectionForChatAPI?.stop();
    this.hubConnectionForChatAPI = undefined;
    this.hubConnectionForChatAPIPromise = undefined;
  }

  disconnectHub() {
    this.connection?.stop();
  }

  getObserveEvent(): Observable<boolean> {
    return this.signalEventReceiveSubject.asObservable();
  }

  getChatObserveEvent(): Observable<boolean> {
    return this.signalChatEventReceiveSubject.asObservable();
  }

  unObserveEvent() {
    if (this.signalEventReceiveSubject.observers) {
      // return this.signalEventReceiveSubject.unsubscribe();
    }
  }

  triggerEvent() {
    this.connection.on("notifyEvent", data => {
      if (this.signalEventReceiveSubject.observers) {
        this.signalEventReceiveSubject.next({ event: 'notifyEvent', result: data });
      }
    });
    this.connection.on("ReceiveEvents", data => {
      if (this.signalEventReceiveSubject.observers) {
        this.signalEventReceiveSubject.next({ event: 'ReceiveEvents', result: data });
      }
    });
    this.connection.on("DispatchingAlertCallBackTrigger", data => {
      if (this.signalEventReceiveSubject.observers) {
        this.signalEventReceiveSubject.next({ event: 'DispatchingAlertCallBackTrigger', result: data });
      }
    });
    this.connection.on("OSCCEmailNotification", data => {
      if (this.signalEventReceiveSubject.observers) {
        this.signalEventReceiveSubject.next({ event: 'OSCCEmailNotification', result: data });
      }
    });
    this.connection.on("eventName", data => {
      if (this.signalEventReceiveSubject.observers) {
        this.signalEventReceiveSubject.next({ event: 'eventName', result: data });
      }
    });
  }

  createChatRoomServerEvents() {
    this.hubConnectionForChatAPI.on(ChatEvents.NEW_MESSAGE, (receivedMessage) => {
      console.log(ChatEvents.NEW_MESSAGE);//, JSON.stringify(receivedMessage)
      if (this.signalChatEventReceiveSubject.observers) {
        this.signalChatEventReceiveSubject.next({ event: ChatEvents.NEW_MESSAGE, result: receivedMessage });
      }
    });

    this.hubConnectionForChatAPI.on(ChatEvents.GET_ONLINE_USERS, (loggedInUsers) => {
      console.log(ChatEvents.GET_ONLINE_USERS);//, JSON.stringify(loggedInUsers)
      if (this.signalChatEventReceiveSubject.observers) {
        this.signalChatEventReceiveSubject.next({ event: ChatEvents.GET_ONLINE_USERS, result: loggedInUsers });
      }
    });

    this.hubConnectionForChatAPI.on(ChatEvents.USER_DETAILS, (userDetails) => {
      console.log(ChatEvents.USER_DETAILS);//, JSON.stringify(userDetails)
      encryptData(userDetails, ChatEvents.USER_DETAILS);
      if (this.signalChatEventReceiveSubject.observers) {
        this.signalChatEventReceiveSubject.next({ event: ChatEvents.USER_DETAILS, result: userDetails });
      }
    })

    this.hubConnectionForChatAPI.on(ChatEvents.ADDED_IN_GROUP, (groupDetails) => {
      console.log(ChatEvents.ADDED_IN_GROUP);//, JSON.stringify(groupDetails)
      if (this.signalChatEventReceiveSubject.observers) {
        this.signalChatEventReceiveSubject.next({ event: ChatEvents.ADDED_IN_GROUP, result: groupDetails });
      }
    })

    this.hubConnectionForChatAPI.on(ChatEvents.BROADCAST_CREATED, (broadastDetails) => {
      console.log(ChatEvents.BROADCAST_CREATED);//, JSON.stringify(broadastDetails)
      if (this.signalChatEventReceiveSubject.observers) {
        this.signalChatEventReceiveSubject.next({ event: ChatEvents.BROADCAST_CREATED, result: broadastDetails });
      }
    })

    this.hubConnectionForChatAPI.on(ChatEvents.ON_DISCONNECT, (disconnectedData) => {
      console.log(ChatEvents.ON_DISCONNECT);//, JSON.stringify(disconnectedData)
      if (this.signalChatEventReceiveSubject.observers) {
        this.signalChatEventReceiveSubject.next({ event: ChatEvents.ON_DISCONNECT, result: disconnectedData });
      }
    })

    this.hubConnectionForChatAPI.on(ChatEvents.REMOVED_FROM_GROUP, (removedParticipantsData) => {
      console.log(ChatEvents.REMOVED_FROM_GROUP);//, JSON.stringify(removedParticipantsData)
      if (this.signalChatEventReceiveSubject.observers) {
        this.signalChatEventReceiveSubject.next({ event: ChatEvents.REMOVED_FROM_GROUP, result: removedParticipantsData });
      }
    })

    this.hubConnectionForChatAPI.on(ChatEvents.DOCUMENT_RECEIVED, (documentReceivedData) => {
      console.log(ChatEvents.DOCUMENT_RECEIVED);//, JSON.stringify(documentReceivedData)
      if (this.signalChatEventReceiveSubject.observers) {
        this.signalChatEventReceiveSubject.next({ event: ChatEvents.DOCUMENT_RECEIVED, result: documentReceivedData });
      }
    })
  }

  createSalesAPIServerEvents() {
    this.hubConnectionForSalesAPI.on("notifyEvent", data => {
      console.log('Sales API: notifyEvent received', data);
      if (this.signalEventReceiveSubject.observers) {
        this.signalEventReceiveSubject.next({ event: 'notifyEvent', result: data });
      }
    });

    this.hubConnectionForSalesAPI.on("ReceiveEvents", data => {
      console.log('Sales API: ReceiveEvents received', data);
      if (this.signalEventReceiveSubject.observers) {
        this.signalEventReceiveSubject.next({ event: 'ReceiveEvents', result: data });
      }
    });

    this.hubConnectionForSalesAPI.on("DispatchingAlertCallBackTrigger", data => {
      console.log('Sales API: DispatchingAlertCallBackTrigger received', data);
      if (this.signalEventReceiveSubject.observers) {
        this.signalEventReceiveSubject.next({ event: 'DispatchingAlertCallBackTrigger', result: data });
      }
    });

    this.hubConnectionForSalesAPI.on("OSCCEmailNotification", data => {
      console.log('Sales API: OSCCEmailNotification received', data);
      if (this.signalEventReceiveSubject.observers) {
        this.signalEventReceiveSubject.next({ event: 'OSCCEmailNotification', result: data });
      }
    });

    this.hubConnectionForSalesAPI.on("eventName", data => {
      console.log('Sales API: eventName received', data);
      if (this.signalEventReceiveSubject.observers) {
        this.signalEventReceiveSubject.next({ event: 'eventName', result: data });
      }
    });
  }

  // Handle user logout - close all connections
  handleUserLogout() {
    console.log('User logout detected, closing all hub connections...');
    this.closeAllHubConnections();
    this.resetSalesAPIConnectionAttempts(); // Reset connection state on logout
  }
}